import { tg } from './telegram';

/** Native keyboards have no cross-platform event: require an editable focus AND
 * a substantial viewport contraction. Retain the closed baseline during blur /
 * keyboard animation; Telegram may shrink viewportStableHeight as well. */
export function watchKeyboard(): () => void {
  const viewport = window.visualViewport;
  const app = tg();
  const visualHeight = () => viewport && viewport.scale === 1 ? viewport.height : window.innerHeight;
  const telegramHeight = () => app?.viewportHeight || window.innerHeight;
  let closedVisual = visualHeight();
  let closedTelegram = telegramHeight();
  let width = window.innerWidth;
  const closedByWidth = new Map<number, { visual: number; telegram: number }>();
  let open = false;
  let frame = 0;

  const editable = () => {
    const element = document.activeElement;
    return element instanceof HTMLElement && (
      element.isContentEditable || element instanceof HTMLTextAreaElement && !element.readOnly && !element.disabled ||
      element instanceof HTMLInputElement && !element.readOnly && !element.disabled &&
        !['button', 'submit', 'reset', 'checkbox', 'radio', 'range', 'color', 'file', 'hidden'].includes(element.type)
    );
  };
  const update = () => {
    const visual = visualHeight();
    const telegram = telegramHeight();
    const focused = editable();
    if (Math.abs(window.innerWidth - width) > 80) {
      // A rotation swaps the closed dimensions, not the keyboard-shrunken ones.
      // Prefer a measured baseline at the destination width; on its first visit
      // the previous width estimates the new closed height. Never carry the old
      // portrait height into landscape, or closing there would stay hidden.
      const rotated = Math.abs(window.innerWidth - closedVisual) <= 80 ||
        Math.abs(window.innerWidth - closedTelegram) <= 80;
      if (open && rotated) {
        const closed = closedByWidth.get(window.innerWidth);
        closedVisual = closed?.visual ?? width;
        closedTelegram = closed?.telegram ?? width;
      } else {
        // Ordinary window resizing / rotation without a keyboard is not evidence.
        closedVisual = visual;
        closedTelegram = telegram;
        open = false;
      }
    }
    width = window.innerWidth;
    const contracted = closedVisual - visual > Math.max(120, closedVisual * 0.18) ||
      closedTelegram - telegram > Math.max(120, closedTelegram * 0.18);
    open = contracted && (focused || open);
    document.documentElement.toggleAttribute('data-keyboard-open', open);
    if (!open && !focused) {
      closedVisual = visual;
      closedTelegram = telegram;
    } else if (!open) {
      closedVisual = Math.max(closedVisual, visual);
      closedTelegram = Math.max(closedTelegram, telegram);
    }
    if (!open) closedByWidth.set(width, { visual: closedVisual, telegram: closedTelegram });
  };
  const schedule = () => {
    cancelAnimationFrame(frame);
    frame = requestAnimationFrame(update);
  };
  viewport?.addEventListener('resize', schedule);
  window.addEventListener('resize', schedule);
  document.addEventListener('focusin', schedule);
  document.addEventListener('focusout', schedule);
  app?.onEvent?.('viewportChanged', schedule);
  update();
  return () => {
    cancelAnimationFrame(frame);
    viewport?.removeEventListener('resize', schedule);
    window.removeEventListener('resize', schedule);
    document.removeEventListener('focusin', schedule);
    document.removeEventListener('focusout', schedule);
    app?.offEvent?.('viewportChanged', schedule);
    document.documentElement.removeAttribute('data-keyboard-open');
  };
}
